/**
 * TPL-006: Runtime validation for slide payloads.
 *
 * This module provides validators that catch malformed slide data at the boundary
 * between Python deck generation and frontend rendering. It prevents cryptic
 * runtime errors by failing fast with actionable messages.
 *
 * Validation is performed in two layers:
 * 1. Handwritten type validators (fast, specific checks)
 * 2. JSON Schema limits validation (maxItems, maxLength from contracts)
 *
 * Usage:
 *   const result = validateSlide(slideData);
 *   if (!result.valid) {
 *     console.error('Slide validation failed:', result.errors);
 *     // Use fallback rendering
 *   }
 */

import type { SlideData, DegradationReason } from '@digital-twins/deck-ui/slides/contracts';
import { validateAgainstJsonSchema, validateStoredPlot, hasSchema, listSchemaTypes } from './schema-loader';
import { SLIDE_TYPE_ALIASES } from './slide-type-aliases';

export interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
  warnings: ValidationError[];
}

export interface ValidationError {
  path: string;           // JSON path to the invalid field
  expected: string;       // Expected type/value
  received: string;       // Actual type/value
  message: string;        // Human-readable error
}

// =============================================================================
// Base validators
// =============================================================================

function isString(value: unknown, path: string): ValidationError[] {
  if (typeof value !== 'string') {
    return [{
      path,
      expected: 'string',
      received: typeof value,
      message: `Expected string at ${path}, got ${typeof value}`,
    }];
  }
  return [];
}

function isObject(value: unknown, path: string): ValidationError[] {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return [{
      path,
      expected: 'object',
      received: value === null ? 'null' : Array.isArray(value) ? 'array' : typeof value,
      message: `Expected object at ${path}, got ${value === null ? 'null' : typeof value}`,
    }];
  }
  return [];
}

// =============================================================================
// Slide type validators
// =============================================================================

const SLIDE_VALIDATORS: Record<string, (slide: unknown) => ValidationError[]> = {};

const LEGACY_SCHEMA_ALIASES = SLIDE_TYPE_ALIASES;

const v2SchemaValidatorFor = (schemaType: string) => (slide: unknown) => {
  const errors: ValidationError[] = [];
  const s = slide as Record<string, unknown>;
  errors.push(...isString(s.type, 'type'));
  if (typeof s.type !== 'string') {
    return errors;
  }

  errors.push(...validateAgainstJsonSchema(schemaType, s));

  return errors;
};

for (const t of listSchemaTypes()) {
  SLIDE_VALIDATORS[t] = v2SchemaValidatorFor(t);
}

// =============================================================================
// Main validation function
// =============================================================================

/**
 * Validate a single slide payload.
 *
 * Performs two layers of validation:
 * 1. Type-specific handwritten validators (fast structural checks)
 * 2. JSON Schema limits validation (maxItems, maxLength from contracts)
 *
 * @param slide - The slide data to validate
 * @returns ValidationResult with errors and warnings
 */
export function validateSlide(slide: unknown): ValidationResult {
  const errors: ValidationError[] = [];
  const warnings: ValidationError[] = [];

  // Basic shape validation
  const objErrors = isObject(slide, 'slide');
  if (objErrors.length > 0) {
    return { valid: false, errors: objErrors, warnings: [] };
  }

  const s = slide as Record<string, unknown>;

  // Type is required
  const typeErrors = isString(s.type, 'type');
  if (typeErrors.length > 0) {
    errors.push(...typeErrors);
    return { valid: false, errors, warnings };
  }

  const slideType = s.type as string;
  const schemaType = LEGACY_SCHEMA_ALIASES[slideType] ?? slideType

  // Run type-specific validator if available
  const validator = SLIDE_VALIDATORS[slideType] ?? SLIDE_VALIDATORS[schemaType];
  if (validator) {
    errors.push(...validator(slide));
  } else {
    // Unknown slide type - just warn, don't fail
    warnings.push({
      path: 'type',
      expected: Object.keys(SLIDE_VALIDATORS).join(' | '),
      received: slideType,
      message: `Unknown slide type '${slideType}' - no specific validator available`,
    });
  }

  // A stored plot that breaks its kind's contract is a warning, never an
  // error: the slide still renders from its legacy fields
  // (validateSlideData drops the plot).
  if (errors.length === 0) {
    warnings.push(...validateStoredPlot(s));
  }

  if (!hasSchema(schemaType)) {
    warnings.push({
      path: 'schema',
      expected: 'schema definition',
      received: 'missing',
      message: `Missing JSON schema for slide type '${slideType}'`,
    });
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Validate an entire deck's slides.
 * 
 * @param slides - Array of slide data
 * @returns Object with validation results per slide
 */
export function validateDeckSlides(slides: unknown[]): {
  valid: boolean;
  slideResults: Array<{ index: number; result: ValidationResult }>;
  totalErrors: number;
  totalWarnings: number;
} {
  const slideResults = slides.map((slide, index) => ({
    index,
    result: validateSlide(slide),
  }));
  
  const totalErrors = slideResults.reduce((sum, r) => sum + r.result.errors.length, 0);
  const totalWarnings = slideResults.reduce((sum, r) => sum + r.result.warnings.length, 0);
  
  return {
    valid: totalErrors === 0,
    slideResults,
    totalErrors,
    totalWarnings,
  };
}

/**
 * Convert validation errors to degradation reasons for consistent tracking.
 */
export function validationErrorsToDegradation(
  errors: ValidationError[],
  slideType?: string,
  slideId?: string,
): DegradationReason[] {
  return errors.map(e => ({
    code: 'SLIDE_VALIDATION_ERROR',
    message: e.message,
    slideType,
    slideId,
    context: {
      path: e.path,
      expected: e.expected,
      received: e.received,
    },
  }));
}
